import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Check, Star, CreditCard, ExternalLink, Loader2, ChevronDown, ArrowRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  interval: string;
  description: string;
  features: Record<string, string | boolean>;
  popular?: boolean;
  stripePrice: string;
}

interface FeatureRow {
  label: string;
  key: string;
}

const featureRows: FeatureRow[] = [
  { label: "Workspaces per user", key: "workspaces" },
  { label: "Users per workspace", key: "users" },
  { label: "Tasks per workspace", key: "tasks" },
  { label: "Support", key: "support" },
  { label: "Classroom Sync", key: "classroomSync" },
  { label: "Public Form Access", key: "publicForms" },
  { label: "SSO", key: "sso" },
  { label: "Custom Development", key: "customDev" },
];

// Mobile-specific features to highlight
const mobileFeatureRows: FeatureRow[] = [
  { label: "Workspaces per user", key: "workspaces" },
  { label: "Users per workspace", key: "users" },
  { label: "Tasks per workspace", key: "tasks" },
  { label: "Support", key: "support" },
  { label: "Classroom Sync", key: "classroomSync" },
];

const plans: PricingPlan[] = [
  {
    id: "free",
    name: "Free",
    price: 0,
    interval: "month",
    description: "Get started with basic features",
    features: {
      workspaces: "1",
      users: "2",
      tasks: "1,000",
      support: "Community",
      classroomSync: true,
      publicForms: false,
      sso: false,
      customDev: false,
    },
    stripePrice: "",
  },
  {
    id: "starter",
    name: "Starter",
    price: 10,
    interval: "month",
    description: "Perfect for small teams getting started",
    features: {
      workspaces: "3",
      users: "5",
      tasks: "5,000",
      support: "Priority",
      classroomSync: true,
      publicForms: true,
      sso: false,
      customDev: false,
    },
    popular: true,
    stripePrice: "price_1RuH2yPb8yc2OyoyFiLuRoE2",
  },
  {
    id: "professional", 
    name: "Professional",
    price: 35,
    interval: "month",
    description: "Best for growing businesses",
    features: {
      workspaces: "5",
      users: "10",
      tasks: "10,000",
      support: "Dedicated",
      classroomSync: true,
      publicForms: true,
      sso: true,
      customDev: true,
    },
    stripePrice: "price_1RuH4RPb8yc2OyoytiHpCcTe",
  },
];

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier?: string;
  subscription_status?: string;
  subscription_end?: string;
}

export function SubscriptionPlans() {
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollToAnchor } = useSmoothScroll();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState<string | null>(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const fetchSubscriptionStatus = async () => {
    if (!user) return;
    
    try {
      setIsRefreshing(true);
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (!error) {
        setSubscriptionData(data);
      } else {
        console.error('Error checking subscription:', error);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [user]);

  const isCurrentPlan = (planId: string): boolean => {
    if (planId === 'free') {
      return !subscriptionData?.subscribed || subscriptionData?.subscription_status === 'canceled';
    }
    
    if (!subscriptionData?.subscribed || subscriptionData?.subscription_status === 'canceled') {
      return false;
    }
    
    const tier = subscriptionData.subscription_tier;
    if (!tier) return false;
    
    return (
      (planId === 'starter' && (tier.includes('Starter') || tier === 'Basic')) ||
      (planId === 'professional' && (tier.includes('Professional') || tier === 'Premium'))
    );
  };

  const getCurrentTier = (): string => {
    if (!subscriptionData?.subscribed || subscriptionData?.subscription_status === 'canceled') {
      return 'free';
    }
    const tier = subscriptionData.subscription_tier;
    if (!tier) return 'free';
    if (tier.includes('Professional') || tier === 'Premium') return 'professional';
    if (tier.includes('Starter') || tier === 'Basic') return 'starter';
    return 'free';
  };

  const getButtonText = (planId: string): string => {
    const currentTier = getCurrentTier();
    const isCurrent = isCurrentPlan(planId);
    
    if (isCurrent) {
      return planId === 'free' ? 'Current Plan' : 'Manage Plan';
    }
    
    const tierOrder = ['free', 'starter', 'professional'];
    const currentIndex = tierOrder.indexOf(currentTier);
    const planIndex = tierOrder.indexOf(planId);
    
    if (planIndex > currentIndex) {
      return 'Upgrade Plan';
    } else {
      return 'Downgrade';
    }
  };

  const hasActiveSubscription = subscriptionData?.subscribed && subscriptionData?.subscription_status !== 'canceled';

  const handleSubscribe = async (plan: PricingPlan) => {
    setIsSubscriptionLoading(plan.id);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId: plan.stripePrice,
          successUrl: `${window.location.origin}/dashboard?success=true`,
          cancelUrl: `${window.location.origin}/dashboard?canceled=true`
        }
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: "Failed to start checkout process. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubscriptionLoading(null);
    }
  };

  const openCustomerPortal = async () => {
    setIsPortalLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error", 
        description: "Failed to open customer portal. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsPortalLoading(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card id="available-plans">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Available Plans
                </CardTitle>
                <CardDescription>
                  Choose the plan that works best for your team
                </CardDescription>
              </div>
              <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-6">
            {isMobile ? (
              // Mobile Layout - Stacked Plan Cards
              <div className="space-y-4">
                {plans.map((plan) => {
                  const isCurrent = isCurrentPlan(plan.id);
                  return (
                    <Card 
                      key={plan.id} 
                      className={`relative ${plan.popular ? 'border-primary ring-1 ring-primary' : ''}`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-4">
                          <Badge className="bg-primary text-primary-foreground">
                            <Star className="h-3 w-3 mr-1" />
                            Most Popular
                          </Badge>
                        </div>
                      )}
                      <CardContent className="pt-6">
                        {/* Plan Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold">{plan.name}</h3>
                            <p className="text-sm text-muted-foreground">{plan.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">${plan.price}</div>
                            <div className="text-xs text-muted-foreground">per member / {plan.interval}</div>
                          </div>
                        </div>

                        {/* Feature List */}
                        <div className="space-y-3 mb-4 py-4 border-y">
                          {mobileFeatureRows.map((featureRow) => {
                            const value = plan.features[featureRow.key];
                            return (
                              <div key={featureRow.key} className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">{featureRow.label}</span>
                                <span className="font-medium">
                                  {typeof value === 'boolean' ? (
                                    value ? (
                                      <Check className="h-4 w-4 text-primary" />
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )
                                  ) : (
                                    value
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Action Button */}
                        {isCurrent && plan.id === 'free' ? (
                          <Button
                            variant="outline"
                            disabled
                            className="w-full"
                          >
                            Current Plan
                          </Button>
                        ) : isCurrent ? (
                          <Button
                            onClick={openCustomerPortal}
                            disabled={isPortalLoading}
                            className="w-full"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            {getButtonText(plan.id)}
                          </Button>
                        ) : plan.id === 'free' ? (
                          <Button
                            variant="outline"
                            onClick={openCustomerPortal}
                            disabled={isPortalLoading}
                            className="w-full"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            {getButtonText(plan.id)}
                          </Button>
                        ) : hasActiveSubscription ? (
                          <Button
                            variant="outline"
                            onClick={openCustomerPortal}
                            disabled={isPortalLoading}
                            className="w-full"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            {getButtonText(plan.id)}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleSubscribe(plan)}
                            disabled={isSubscriptionLoading === plan.id}
                            className="w-full"
                          >
                            {isSubscriptionLoading === plan.id ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <CreditCard className="h-4 w-4 mr-2" />
                                {getButtonText(plan.id)}
                              </>
                            )}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Compare More Features Button */}
                <div className="flex justify-center pt-4">
                  <Button 
                    variant="outline" 
                    onClick={(e) => {
                      e.preventDefault();
                      if (location.pathname !== "/") {
                        navigate("/");
                        setTimeout(() => scrollToAnchor("features"), 100);
                      } else {
                        scrollToAnchor("features");
                      }
                    }}
                  >
                    Compare more features
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              // Desktop Layout - Grid Comparison Table
              <div className="space-y-8">
                {/* Plan Headers */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="font-medium text-muted-foreground">Highlights</div>
                  {plans.map((plan) => {
                    const isCurrent = isCurrentPlan(plan.id);
                    return (
                      <div key={plan.id} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-semibold">{plan.name}</h3>
                          {plan.popular && (
                            <Badge variant="secondary" className="text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              Popular
                            </Badge>
                          )}
                        </div>
                        <div>
                          <div className="text-2xl font-bold">${plan.price}</div>
                          <div className="text-sm text-muted-foreground">per member / {plan.interval}</div>
                        </div>
                        
                        {/* Action Button */}
                        {isCurrent && plan.id === 'free' ? (
                          <Button
                            variant="outline"
                            disabled
                            className="w-full"
                          >
                            Current Plan
                          </Button>
                        ) : isCurrent ? (
                          <Button
                            onClick={openCustomerPortal}
                            disabled={isPortalLoading}
                            className="w-full"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            {getButtonText(plan.id)}
                          </Button>
                        ) : plan.id === 'free' ? (
                          <Button
                            variant="outline"
                            onClick={openCustomerPortal}
                            disabled={isPortalLoading}
                            className="w-full"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            {getButtonText(plan.id)}
                          </Button>
                        ) : hasActiveSubscription ? (
                          <Button
                            variant="outline"
                            onClick={openCustomerPortal}
                            disabled={isPortalLoading}
                            className="w-full"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            {getButtonText(plan.id)}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleSubscribe(plan)}
                            disabled={isSubscriptionLoading === plan.id}
                            className="w-full"
                          >
                            {isSubscriptionLoading === plan.id ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <CreditCard className="h-4 w-4 mr-2" />
                                {getButtonText(plan.id)}
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Feature Rows */}
                <div className="space-y-4 border-t pt-6">
                  {featureRows.map((featureRow) => (
                    <div key={featureRow.key} className="grid grid-cols-4 gap-4 items-center">
                      <div className="text-sm font-medium">{featureRow.label}</div>
                      {plans.map((plan) => {
                        const value = plan.features[featureRow.key];
                        return (
                          <div key={plan.id} className="flex justify-center">
                            {typeof value === 'boolean' ? (
                              value ? (
                                <Check className="h-5 w-5 text-primary" />
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )
                            ) : (
                              <span className="text-sm">{value}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Compare More Features Button */}
                <div className="flex justify-center pt-6">
                  <Button 
                    variant="outline" 
                    onClick={(e) => {
                      e.preventDefault();
                      if (location.pathname !== "/") {
                        navigate("/");
                        setTimeout(() => scrollToAnchor("features"), 100);
                      } else {
                        scrollToAnchor("features");
                      }
                    }}
                  >
                    Compare more features
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}